import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { MantineEmotionProvider } from "@mantine/emotion";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import { theme } from "./utils/theme";
import Header from "./components/Header";
import Footer from "./components/Footer";
import App from "./App";
import HeaderAuth from "./components/HeaderAuth";

const setCSSVariables = (theme) => {
  const root = document.documentElement;

  Object.entries(theme.fontFamily).forEach(([key, value]) => {
    root.style.setProperty(`--fontFamily-${key}`, value);
  });

  Object.entries(theme.customColors).forEach(([key, value]) => {
    root.style.setProperty(`--customColors-${key}`, value);
  });

  Object.entries(theme.customBackgrounds).forEach(([key, value]) => {
    root.style.setProperty(`--customBackgrounds-${key}`, value);
  });
};

setCSSVariables(theme);

const props = window.props || {};
const staticBasePath = "/static/";

const headerRootMount = document.getElementById("header-root");
const headerAuthRootMount = document.getElementById("header-auth-root");
const footerRootMount = document.getElementById("footer-root");

function getValue(key) {
  const element = document.getElementById(key);
  if (element) {
    // Clean up the value by trimming and removing surrounding quotes
    let cleanedText = element.textContent.trim();
    if (cleanedText.startsWith('"') && cleanedText.endsWith('"')) {
      cleanedText = cleanedText.slice(1, -1);
    }
    return cleanedText;
  }
  return "";
}

function getValueAsBoolean(key) {
  const element = document.getElementById(key);
  if (element) {
    const trimmedText = element.textContent.trim().toLowerCase();
    return trimmedText === "true";
  }
  return false;
}

// Get initial values from HTML (fallback for initial render)
const initialUserIsAuthenticated =
  getValueAsBoolean("user-is-authenticated") || false;
const userEmail = getValue("user-email");
const userId = getValue("user-id");
const subscriberId = getValue("subscriber-id");
const paddleCustomerId = getValue("paddle-customer-id");
const paddleOwnershipToken = getValue("paddle-ownership-token");
const userShortName = getValue("user-short-name");
const userNameValue = getValue("user-name");

const djangoContext = {
  userIsAuthenticated: initialUserIsAuthenticated, // Initial value, will be updated dynamically
  userEmail,
  userId,
  subscriberId,
  paddleCustomerId,
  paddleOwnershipToken,
  userShortName,
  userNameValue,
};

if (headerRootMount) {
  const root = createRoot(headerRootMount);
  root.render(
    <BrowserRouter>
      <MantineEmotionProvider>
        <MantineProvider withGlobalStyles withNormalizeCSS theme={theme}>
          <Header
            {...props}
            staticBasePath={staticBasePath}
            djangoContext={djangoContext}
          />
        </MantineProvider>
      </MantineEmotionProvider>
    </BrowserRouter>,
  );
}

if (footerRootMount) {
  const root = createRoot(footerRootMount);
  root.render(
    <BrowserRouter>
      <MantineEmotionProvider>
        <MantineProvider withGlobalStyles withNormalizeCSS theme={theme}>
          <Footer {...props} staticBasePath={staticBasePath} />
        </MantineProvider>
      </MantineEmotionProvider>
    </BrowserRouter>,
  );
}

if (headerAuthRootMount) {
  const root = createRoot(headerAuthRootMount);
  root.render(
    <MantineEmotionProvider>
      <MantineProvider withGlobalStyles withNormalizeCSS theme={theme}>
        <HeaderAuth
          {...props}
          staticBasePath={staticBasePath}
          djangoContext={djangoContext}
        />
      </MantineProvider>
    </MantineEmotionProvider>,
  );
}

const rootEl = document.getElementById("root");
if (rootEl) {
  const root = createRoot(rootEl);
  root.render(
    <BrowserRouter>
      <MantineEmotionProvider>
        <App
          props={props}
          djangoContext={djangoContext}
          staticBasePath={staticBasePath}
          theme={theme}
        />
      </MantineEmotionProvider>
    </BrowserRouter>,
  );
}
